package br.com.htms.inspeccaocampo.data.checklist

import br.com.htms.inspeccaocampo.data.ErroCampo
import br.com.htms.inspeccaocampo.data.FotoAnexo
import br.com.htms.inspeccaocampo.data.FormTipo
import br.com.htms.inspeccaocampo.data.ItemConformidade
import br.com.htms.inspeccaocampo.data.RespostaConformidade
import br.com.htms.inspeccaocampo.platform.currentTimeMillis
import br.com.htms.inspeccaocampo.platform.formatDateBr
import br.com.htms.inspeccaocampo.platform.monthOf
import br.com.htms.inspeccaocampo.platform.yearOf
import br.com.htms.inspeccaocampo.shared.ui.components.DateFieldKind
import br.com.htms.inspeccaocampo.shared.ui.components.DateInputMask

data class ChecklistTipo(
    val id: String,
    val nome: String,
    val titulo: String,
    val itens: List<ChecklistItemDef>
)

data class ChecklistItemDef(
    val numero: Int,
    val descricao: String,
    val observacaoFoto: String? = null,
    val exigeObs: Boolean = false,
    val exigeFoto: Boolean = false,
    val exigeDescricaoNc: Boolean = true,
    val exigeFotoNc: Boolean = true
) {
    fun orientacaoFoto(): String =
        observacaoFoto ?: ChecklistFotoObservacoes.gerar(descricao)
}

object ChecklistFotoObservacoes {
    fun gerar(descricao: String): String {
        val lower = descricao.lowercase()
        return when {
            lower.contains("cnh") -> "Foto dos documentos"
            lower.contains("extintor") -> "Foto do extintor (carga, validade e fixação)"
            lower.contains("pneu") -> "Foto dos pneus"
            lower.contains("logotipo") -> "Foto do logotipo da empresa"
            else -> "Foto evidenciando o item"
        }
    }

    fun precisaMultiplasFotos(descricao: String): Boolean {
        val lower = descricao.lowercase()
        return lower.contains("pneu") || lower.contains("lanterna") || lower.contains("farol")
    }
}

enum class ChecklistDiarioMarca(val id: String, val titulo: String) {
    HTMS("htms", "Inspeção HTMS"),
    AXIA("axia", "Inspeção Axia");

    fun tipos(): List<ChecklistTipo> = when (this) {
        HTMS -> ChecklistCatalog.tipos
        AXIA -> AxiaChecklistCatalog.tipos
    }

    fun formTipo(): FormTipo = when (this) {
        HTMS -> FormTipo.CHECKLIST_DIARIO_HTMS
        AXIA -> FormTipo.CHECKLIST_DIARIO_AXIA
    }

    fun rotuloCondutor(tipoId: String): String = when (this) {
        HTMS -> ChecklistCatalog.rotuloCondutor(tipoId)
        AXIA -> AxiaChecklistCatalog.rotuloCondutor(tipoId)
    }

    companion object {
        fun fromFormTipo(tipo: FormTipo): ChecklistDiarioMarca =
            if (tipo == FormTipo.CHECKLIST_DIARIO_AXIA) AXIA else HTMS

        fun fromFormTipoId(formTipoId: String): ChecklistDiarioMarca =
            if (formTipoId.contains("AXIA", ignoreCase = true)) AXIA else HTMS

        fun fromId(raw: String?): ChecklistDiarioMarca =
            entries.find { it.id.equals(raw, ignoreCase = true) } ?: HTMS
    }
}

data class FormChecklistDiario(
    val marca: String = ChecklistDiarioMarca.HTMS.id,
    val tipoId: String = "",
    val tipoNome: String = "",
    val tipoTitulo: String = "",
    val obra: String = "",
    val mesAno: String = "",
    val numeroChecklist: String = "1",
    val dataHorarioMs: Long = currentTimeMillis(),
    val proprietario: String = "",
    val modelo: String = "",
    val placa: String = "",
    val combustivel: String = "",
    val motoristaNome: String = "",
    val motoristaFuncao: String = "",
    val motoristaCnh: String = "",
    val motoristaValidade: String = "",
    val preenchidoPor: String = "",
    /** Campos específicos Axia (mobilização). */
    val fornecedorCliente: String = "",
    val contrato: String = "",
    val validadeContrato: String = "",
    val gestorContrato: String = "",
    val localidadeMobilizacao: String = "",
    val areaAcesso: String = "",
    val anoFabricacao: String = "",
    val tipoVeiculo: String = "",
    val observacoesGerais: String = "",
    val aprovado: Boolean? = null,
    /** Fotos/documentos do plano de manutenção (Axia — requisitos MEV). */
    val fotosPlanoManutencao: List<FotoAnexo> = emptyList(),
    /** Evidências das manutenções realizadas (Axia — requisitos MEV). */
    val fotosEvidenciasManutencao: List<FotoAnexo> = emptyList(),
    /** CRLV impresso/eletrônico (Axia — obrigatório quando o tipo exige CRLV). */
    val fotosCrlv: List<FotoAnexo> = emptyList(),
    /** Fotos das Observações Gerais (Axia). */
    val fotosObservacoes: List<FotoAnexo> = emptyList(),
    val itens: List<ItemConformidade> = emptyList(),
    val latitude: Double? = null,
    val longitude: Double? = null,
    val localTexto: String = "",
    val assinaturaMotorista: String = "",
    val assinaturaEncarregado: String = "",
    val assinaturaSesmt: String = ""
) {
    fun marcaEnum(): ChecklistDiarioMarca = ChecklistDiarioMarca.fromId(marca)

    fun dataHorarioFormatado(): String = formatDateBr(dataHorarioMs)

    fun dataInspecao(): String = dataHorarioFormatado().substringBefore(' ').ifBlank { dataHorarioFormatado() }

    fun horaInspecao(): String {
        val parts = dataHorarioFormatado().split(' ', limit = 2)
        return parts.getOrElse(1) { "" }
    }

    fun mesAnoEfetivo(): String =
        mesAno.ifBlank { mesAnoDe(dataHorarioMs) }

    fun diaDoMes(): Int =
        dataInspecao().substringBefore('/').toIntOrNull()?.coerceIn(1, 31) ?: 1

    fun fotosParaExportacao(): List<Pair<String, FotoAnexo>> = buildList {
        fotosPlanoManutencao.forEachIndexed { index, foto ->
            add("Plano de manutenção — Foto ${index + 1}" to foto)
        }
        fotosEvidenciasManutencao.forEachIndexed { index, foto ->
            add("Evidências de manutenção — Foto ${index + 1}" to foto)
        }
        fotosCrlv.forEachIndexed { index, foto ->
            add("${AxiaRequisitosMev.DOC_CRLV_ROTULO} — Foto ${index + 1}" to foto)
        }
        itens.forEach { item ->
            item.fotos.forEachIndexed { index, foto ->
                add("${item.tituloNumerado} — Foto ${index + 1}" to foto)
            }
        }
        fotosObservacoes.forEachIndexed { index, foto ->
            add("Observações gerais — Foto ${index + 1}" to foto)
        }
    }

    fun totalFotosAnexos(): Int =
        fotosPlanoManutencao.size +
            fotosEvidenciasManutencao.size +
            fotosCrlv.size +
            fotosObservacoes.size +
            itens.sumOf { it.fotos.size }

    companion object {
        private val mesesPt = listOf(
            "jan", "fev", "mar", "abr", "mai", "jun",
            "jul", "ago", "set", "out", "nov", "dez"
        )

        fun mesAnoDe(ms: Long): String {
            val m = monthOf(ms).coerceIn(0, 11)
            val yy = (yearOf(ms) % 100).toString().padStart(2, '0')
            return "${mesesPt[m]}/$yy"
        }

        fun novo(marca: ChecklistDiarioMarca): FormChecklistDiario {
            val ms = currentTimeMillis()
            return FormChecklistDiario(
                marca = marca.id,
                dataHorarioMs = ms,
                mesAno = mesAnoDe(ms),
                numeroChecklist = "1"
            )
        }

        fun itensDoTipo(tipo: ChecklistTipo): List<ItemConformidade> =
            tipo.itens.map { def ->
                ItemConformidade(
                    id = "cd_${tipo.id}_${def.numero}",
                    numero = def.numero.toString(),
                    titulo = def.descricao,
                    secao = tipo.nome,
                    fotoHabilitada = true,
                    fotoObrigatoria = def.exigeFotoNc,
                    obrigatorio = true
                )
            }
    }
}

fun FormChecklistDiario.semConteudoBase64(): FormChecklistDiario = copy(
    fotosPlanoManutencao = fotosPlanoManutencao.map { it.copy(conteudoBase64 = null) },
    fotosEvidenciasManutencao = fotosEvidenciasManutencao.map { it.copy(conteudoBase64 = null) },
    fotosCrlv = fotosCrlv.map { it.copy(conteudoBase64 = null) },
    fotosObservacoes = fotosObservacoes.map { it.copy(conteudoBase64 = null) },
    itens = itens.map { item ->
        item.copy(fotos = item.fotos.map { it.copy(conteudoBase64 = null) })
    }
)

fun FormChecklistDiario.comTipo(tipo: ChecklistTipo): FormChecklistDiario = copy(
    tipoId = tipo.id,
    tipoNome = tipo.nome,
    tipoTitulo = tipo.titulo,
    itens = FormChecklistDiario.itensDoTipo(tipo)
)

object ChecklistDiarioValidacao {
    fun validar(form: FormChecklistDiario): List<ErroCampo> {
        val erros = mutableListOf<ErroCampo>()
        if (form.tipoId.isBlank()) {
            erros += ErroCampo("tipoId", "Selecione o tipo de checklist.")
        }
        if (form.obra.isBlank() && form.marcaEnum() == ChecklistDiarioMarca.HTMS) {
            erros += ErroCampo("obra", "Selecione a obra.")
        }
        if (form.preenchidoPor.isBlank()) {
            erros += ErroCampo("preenchidoPor", "Informe quem preencheu.")
        }
        DateInputMask.mensagemSeInvalido(
            form.motoristaValidade, DateFieldKind.DATE, "a validade da CNH"
        )?.let { erros += ErroCampo("motoristaValidade", it) }
        if (form.marcaEnum() == ChecklistDiarioMarca.AXIA) {
            DateInputMask.mensagemSeInvalido(
                form.validadeContrato, DateFieldKind.DATE, "a validade do contrato"
            )?.let { erros += ErroCampo("validadeContrato", it) }
            DateInputMask.mensagemSeInvalido(
                form.anoFabricacao, DateFieldKind.YEAR, "o ano de fabricação"
            )?.let { erros += ErroCampo("anoFabricacao", it) }
        } else {
            val mes = form.mesAno.trim()
            val mesAuto = mes.matches(Regex("^[A-Za-zÀ-ÿ]{3}/\\d{2}$"))
            if (mes.isNotBlank() && !mesAuto) {
                DateInputMask.mensagemSeInvalido(
                    mes, DateFieldKind.MONTH_YEAR, "o mês/ano"
                )?.let { erros += ErroCampo("mesAno", it) }
            }
        }
        if (form.latitude == null || form.longitude == null) {
            erros += ErroCampo("localGps", "Capture o local por GPS.")
        }
        form.itens.forEach { item ->
            val resp = item.resposta
            if (resp == null) {
                erros += ErroCampo(item.id, "Responda o item ${item.tituloNumerado}.")
                return@forEach
            }
            if (resp == RespostaConformidade.NC) {
                if (item.justificativa.isBlank()) {
                    erros += ErroCampo(item.id, "Descreva a não conformidade do item ${item.numero}.")
                }
                if (item.fotoObrigatoria && item.fotos.isEmpty()) {
                    erros += ErroCampo(item.id, "Anexe foto da NC do item ${item.numero}.")
                }
            }
        }
        if (form.marcaEnum() == ChecklistDiarioMarca.AXIA) {
            if (form.fotosPlanoManutencao.isEmpty()) {
                erros += ErroCampo(
                    "fotosPlanoManutencao",
                    "Anexe foto/documento do plano de manutenção."
                )
            }
            if (form.fotosEvidenciasManutencao.isEmpty()) {
                erros += ErroCampo(
                    "fotosEvidenciasManutencao",
                    "Anexe foto/documento das evidências de manutenção."
                )
            }
            if (AxiaRequisitosMev.exigeCrlv(form.tipoNome) && form.fotosCrlv.isEmpty()) {
                erros += ErroCampo(
                    "fotosCrlv",
                    "Anexe foto/documento do ${AxiaRequisitosMev.DOC_CRLV_ROTULO}."
                )
            }
            if (form.aprovado == null) {
                erros += ErroCampo("aprovado", "Informe se a vistoria foi aprovada ou reprovada.")
            }
            if (form.assinaturaMotorista.isBlank()) {
                erros += ErroCampo(
                    "assinaturaMotorista",
                    "Assinatura do responsável pela vistoria é obrigatória."
                )
            }
        }
        return erros
    }
}
